import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import {
	usePageCopy,
	usePromoteOrShareActionUrls,
	useSetBanditsCookie,
} from '#src/app/pages/psf/promoteOrShare/shared/hooks';

const PAYMENT_METHODS = [
	'applepay',
	'googlepay',
	'visa',
	'mastercard',
	'jcb',
	'discover',
	'paypal',
	'americanexpress',
] as const;

type Result = ModelHookResult<
	{
		promoteButtonLabel: string;
		promoteButtonUrl: string;
		shareButtonLabel: string;
		shareButtonVariant: 'tertiary';
		shareButtonUrl: string;
		paymentMethods: typeof PAYMENT_METHODS;
		isShareSlideUpEnabled: boolean;
		advocateEntryPointExperiment: boolean;
		advocateEntryPointUrl: string;
		advocateEntryPointButtonLabel: string;
	},
	{
		handlePromoteClick: () => void;
		handleShareClick: () => void;
		handleAdvocateEntryPointClick: () => void;
	}
>;

export function usePromoteOrShareActions(): Result {
	const {
		data: {
			isShareSlideUpEnabled,
			advocateEntryPointExperiment,
			advocateEntryPointButtonLabel,
			advocateEntryPointCpDisabled,
			petition: { isCommunityPetition },
		},
	} = usePsfPromoteOrSharePageData();
	const {
		data: { shareButtonText, promoteButtonWithGoalText },
	} = usePageCopy();

	const {
		data: { promoteButtonUrl, shareButtonUrl, advocateEntryPointUrl },
	} = usePromoteOrShareActionUrls();

	return {
		data: {
			promoteButtonLabel: promoteButtonWithGoalText,
			promoteButtonUrl,
			shareButtonLabel: shareButtonText,
			shareButtonVariant: 'tertiary',
			shareButtonUrl,
			paymentMethods: PAYMENT_METHODS,
			isShareSlideUpEnabled,
			advocateEntryPointExperiment:
				!advocateEntryPointCpDisabled && advocateEntryPointExperiment.variation === 'enabled' && isCommunityPetition,
			advocateEntryPointUrl,
			advocateEntryPointButtonLabel,
		},
		actions: useHandlers(),
	};
}

function useHandlers() {
	const track = useTracking();
	const setPsfComboBanditsCookie = useSetBanditsCookie();

	return {
		handlePromoteClick: useCallback(() => {
			setPsfComboBanditsCookie();
			void track('combo_psf_promote', {
				combo_promote_click_outcome: 'navigated',
			});
		}, [setPsfComboBanditsCookie, track]),
		handleShareClick: useCallback(() => {
			void track('combo_psf_share');
		}, [track]),
		handleAdvocateEntryPointClick: useCallback(() => {
			void track('combo_psf_advocate_entry_point');
		}, [track]),
	};
}
